import { useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { getContentStructure, getTemplateCategory, getTemplatePalette, getOnboardingProgress, createOnboardingProgress, updateOnboardingProgress, updateCompanyName } from '@/api/onboarding'
import { transformAIResponse } from '@/helpers/responseTransformer'
import { Queries } from '@/constants/queries'
import { useAppContext } from '../context/AppContext'
import { getAPIErrorMessage } from '../helpers/apiErrorHelper'
import { deleteOnboardingProgress, getAutocompleteSuggestions, getOnboardingChoices, getOnboardingStatusFromStoredash, getPlaceDetails, getSections } from '../api/onboarding'

const useOnboarding = () => {
    const { addEvent } = useAppContext()

    //submit the website details form and get the style type
    const templateCateogryReq = useMutation({
        mutationFn: transformAIResponse(getTemplateCategory, 'arguments'),
        onError: (err) => {
            console.error(err)
            addEvent({
                message: getAPIErrorMessage(err, 'templateCateogryReq'),
                type: 'error'
            })
        }
    })

    const templateContentReq = useMutation({
        mutationFn: transformAIResponse(getContentStructure, 'OpenAiResponse.choices[0].message.content'),
        onError: (err) => {
            console.error(err)
            addEvent({
                message: getAPIErrorMessage(err, 'templateContentReq'),
                type: 'error'
            })
        }
    })

    const templateColorsReq = useQuery({
        queryKey: [Queries.ONBOARDING_PALETTE],
        queryFn: () => getTemplatePalette(),
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
        enabled: false
    })

    useEffect(() => {
        if (templateColorsReq.isError) {
            const err = templateColorsReq.error;
            addEvent({
                message: getAPIErrorMessage(err, 'templateColorsReq'),
                type: 'error',
            });
        }
    }, [templateColorsReq.isError]);

    
    return {
        templateCategoryMutate: templateCateogryReq.mutate,
        templateCategoryisLoading: templateCateogryReq.isPending,
        templateCategoryisError: templateCateogryReq.isError,
        templateContentMutate: templateContentReq.mutate,
        templateContentisLoading: templateContentReq.isPending,
        templateContentisError: templateContentReq.isError,
        templateColors: templateColorsReq.data,
        templateColorsisLoading: templateColorsReq.isLoading,
        templateColorsisError: templateColorsReq.isError,
        fetchTemplateColors: templateColorsReq.refetch
    }
}

export const useGetSavedState = (stateId) => {
    return useQuery({
        queryKey: [Queries.SAVED_STATE, stateId],
        queryFn: () => getOnboardingProgress(stateId),
        enabled: !!stateId,
        retry: false,
        refetchOnWindowFocus: false
    })
}

export const useCreateSavedState = () => {
    return useMutation({
        mutationFn: (payload) => createOnboardingProgress(payload)
    })
}

export const useUpdateSavedState = () => {
    return useMutation({
        mutationFn: (payload) => updateOnboardingProgress(payload)
    })
}

export const useDeleteSavedState = () => {
    return useMutation({
        mutationFn: (id) => deleteOnboardingProgress(id)
    })
}

export const useTemplateColors = (templateId) => {
    return useQuery({
        queryKey: ["COLORS", templateId],
        queryFn: () => getTemplatePalette(templateId),
        enabled: !!templateId,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false
    })
}

export const useGetOnboardingChoices = (productInstanceId) => {
    return useQuery({
        queryKey: [Queries.SAVED_STATE, productInstanceId],
        queryFn: () => getOnboardingChoices(productInstanceId),
        enabled: !!productInstanceId,
        retry: false,
        refetchOnWindowFocus: false
    })
}

export const useOnboardingStatusFromStoredash = (productInstanceId) => {
    return useQuery({
        queryKey: ['STOREDASH_QUERY'],
        queryFn: () => getOnboardingStatusFromStoredash(productInstanceId),
        enabled: !!productInstanceId,
        initialData: {},
        retry: false,
        refetchOnWindowFocus: false
    })
}

export const useAutocompleteSuggestions = (query, sessionId) => {
    return useQuery({
        queryKey: ['AUTOCOMPLETE_SUGGESTIONS', query],
        queryFn: () => getAutocompleteSuggestions(query, sessionId),
        enabled: !!sessionId && query?.length >= 2,
        retry: false,
        refetchOnWindowFocus: false
    })
}

export const useGetPlaceDetails = () => {
    return useMutation({
        mutationFn: ({ placeId, sessionId }) => getPlaceDetails(placeId, sessionId)
    })
}

export const useUpdateCompanyName = () => {
    return useMutation({
        mutationFn: ({ productInstanceId, companyName }) => updateCompanyName(productInstanceId, companyName)
    })
}

export const useGetSections = (projectId, accessToken) => {
    return useQuery({
        queryKey: ['SECTIONS', projectId],
        queryFn: () => getSections(projectId, accessToken),
        enabled: !!accessToken,
        retry: false,
        refetchOnWindowFocus: false
    })
}

export default useOnboarding